// "‹ <wherever you came from>" — a back control that names its destination.
//
// The route is read from navHistoryStore, which records every navigation
// state change, rather than from navigation.goBack(). goBack() is unreliable
// in this app: MainMenu navigates with CommonActions.reset(), which wipes the
// drawer history, and a screen that redirects on mount leaves an entry that
// bounces you straight back. Recording the previous route ourselves means the
// label and the destination are always the same thing.
//
// Falls back to the Dashboard when there's no previous route — a deep link or
// a hard refresh, where there genuinely isn't anywhere to go back to.

import React from 'react';
import {Pressable, StyleProp, ViewStyle} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import {useNavHistoryStore} from '@/Store/navHistoryStore';
import {transition} from '@/Utils/motion';

// Route names are internal ('Resources', 'Positions'); these are what users
// call the same screens. Anything not listed falls through to the route name,
// which is already human-readable for most screens (Dashboard, Settings…).
const ROUTE_LABELS: Record<string, string> = {
  Dashboard: 'Dashboard',
  Resources: 'School of Chess',
  Positions: 'Puzzles',
  VisualisationTrainer: 'Trainers',
  ChessAnalysis: 'Game review',
  PlayChess: 'Play',
  'Rules-and-Rewards': 'Rewards',
};

const labelFor = (route?: string | null) =>
  (route && (ROUTE_LABELS[route] || route)) || 'Dashboard';

// useNavigation() throws when there's no navigation context — screens that are
// rendered directly (unit tests, or anything mounted outside a navigator) pass
// their own `navigation` prop instead. Both useContext calls inside it run
// before it throws, so hook order stays stable and catching is safe.
function useNavigationSafe() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNavigation<any>();
  } catch {
    return null;
  }
}

// Literal, not ScreenName.Dashboard: StackConstants imports every screen in the
// app, so pulling it into a leaf atom drags the whole module graph in wherever
// this is used — enough to blow a unit test's timeout. The value is asserted
// against ROUTE_LABELS below.
const DASHBOARD_ROUTE = 'Dashboard';

interface BackToPreviousProps {
  /** Route to use when nothing was recorded. */
  fallback?: string;
  /** Explicit navigation object, for screens rendered outside a navigator. */
  navigation?: any;
  /**
   * Routes to skip when walking back — for screens that redirect to the caller
   * on mount. ClubRoom bounces non-active clubs to ClubProfile, so offering
   * "back to ClubRoom" from there returns you where you started.
   */
  exclude?: string[];
  style?: StyleProp<ViewStyle>;
}

export default function BackToPrevious({
  fallback = DASHBOARD_ROUTE,
  navigation: navigationProp,
  exclude,
  style,
}: BackToPreviousProps) {
  const contextNavigation = useNavigationSafe();
  const navigation = navigationProp || contextNavigation;
  // Subscribe to the stack so the label re-renders as history changes;
  // getPrevious itself reads the latest state.
  useNavHistoryStore(s => s.stack);
  const previous = useNavHistoryStore.getState().getPrevious(exclude);

  const targetName = previous?.name || fallback;

  return (
    <Pressable
      testID="back-to-previous"
      // Params matter: ClubProfile is 'clubs/profile/:clubId' and crashes
      // outright without one.
      onPress={() => navigation?.navigate?.(targetName, previous?.params)}
      style={({hovered, pressed}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          alignSelf: 'flex-start',
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 20,
          borderWidth: 1,
          borderColor: 'rgba(38,35,34,0.18)',
          backgroundColor: '#fff',
          cursor: 'pointer',
          ...transition(160),
        },
        hovered && {backgroundColor: '#FFFDF6', borderColor: '#E7C55A'},
        pressed && {opacity: 0.8},
        style,
      ]}>
      <CCText style={{fontSize: 16, lineHeight: 16, color: '#4D3F37'}}>‹</CCText>
      <CCText style={{fontSize: 13, fontWeight: '600', color: '#4D3F37'}}>
        {labelFor(targetName)}
      </CCText>
    </Pressable>
  );
}
